import { withLineQuantity } from "../cart-line-utils";
import { giftPromotionItems } from "../promotion-messaging";

import createId from "@/lib/create-id";

import { ItemType } from "@/types/enums/item-type";
import {
  GiftCardShopCartLine,
  PublicShopPromotion,
  RegularCatalogShopCartLine,
  RegularCatalogShopItem,
  ShopCartLine,
  ShopItem,
} from "@/types/shop";

const makeLineId = (): string => createId("cl");

export const lineFromGiftCard = (item: ShopItem): GiftCardShopCartLine => ({
  kind: "preset",
  lineId: makeLineId(),
  itemType: ItemType.GIFT_CARD,
  itemId: item.id,
  unitPriceCents: item.price,
  quantity: 1,
  recipients: [],
  snapshot: {
    name: item.name,
    description: item.description,
    imageUrl: item.imageUrl,
    category: item.category,
  },
});

export const isRegularCatalogShopItem = (
  item: ShopItem,
): item is RegularCatalogShopItem =>
  item.type === ItemType.SERVICE ||
  item.type === ItemType.PRODUCT ||
  item.type === ItemType.MEDICATION;

interface CartWriter {
  lines: ShopCartLine[];
  addLine: (line: ShopCartLine) => boolean;
  updateQuantity: (lineId: string, quantity: number) => void;
}

/**
 * Add units of a product, medication, or service, topping up its cart line if
 * one exists. False when the cart refused it (e.g. it holds a membership).
 */
export function addRegularItemToCart(
  cart: CartWriter,
  item: RegularCatalogShopItem,
  quantity: number,
): boolean {
  const existing = cart.lines.find(
    (line) =>
      line.kind === "catalog-item" &&
      line.itemType === item.type &&
      line.itemId === item.id,
  );
  if (existing) {
    cart.updateQuantity(existing.lineId, existing.quantity + quantity);
    return true;
  }
  return cart.addLine(withLineQuantity(lineFromRegularItem(item), quantity));
}

export const lineFromRegularItem = (
  item: RegularCatalogShopItem,
): RegularCatalogShopCartLine => ({
  kind: "catalog-item",
  lineId: makeLineId(),
  itemType: item.type,
  itemId: item.id,
  unitPriceCents: item.price,
  expectedUnitPriceCents: item.price,
  quantity: 1,
  snapshot: {
    name: item.name,
    description: item.description,
    imageUrl: item.imageUrl,
    category: item.category,
    requiresPickupLocation: item.requiresPickupLocation,
  },
});

/** One unit of a free item hidden from the shop, built from its offer. */
export const lineFromGiftOffer = (
  offer: PublicShopPromotion,
): RegularCatalogShopCartLine | null => {
  const [item] = giftPromotionItems([offer]);
  return item && isRegularCatalogShopItem(item)
    ? { ...lineFromRegularItem(item), freeGift: true }
    : null;
};
